// src/modules/chat/ChatWindow.jsx
// Individual floating chat window — streaming tokens, stop button, markdown,
// platform context bar, configure nudge

import { useState, useRef, useEffect, useCallback } from 'react';
import { getEntity, ENTITY_TYPES, formatTime } from './chatStore';
import { useChat } from './ChatContext';

const MONO = 'JetBrains Mono,monospace';

const TYPE_PILL = {
  [ENTITY_TYPES.MY_AI]:     { label:'AGENT',    bg:'#22d3ee18', border:'#22d3ee44', color:'#22d3ee' },
  [ENTITY_TYPES.PUBLIC_AI]: { label:'PLATFORM', bg:'#a78bfa18', border:'#a78bfa44', color:'#a78bfa' },
  [ENTITY_TYPES.HUMAN]:     { label:'HUMAN',    bg:'#4ade8018', border:'#4ade8044', color:'#4ade80' },
};

// ── Markdown-lite renderer: **bold**, `code`, ```blocks``` ────────────────────
function MdText({ text, streaming }) {
  if (!text && !streaming) return null;

  const parts = (text || '').split(/(```[\s\S]*?```|`[^`]+`|\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          const inner = part.slice(3, -3).replace(/^\w+\n/, '');
          return (
            <pre key={i} style={{
              background: '#020508', border: '1px solid #1e293b', borderRadius: 3,
              padding: '8px 10px', fontSize: 9, color: '#a78bfa',
              fontFamily: MONO, overflowX: 'auto',
              margin: '5px 0', lineHeight: 1.65,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{inner}</pre>
          );
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={i} style={{
              background: '#0a1120', border: '1px solid #1e293b',
              borderRadius: 2, padding: '1px 5px',
              fontSize: 9.5, color: '#22d3ee', fontFamily: MONO,
            }}>{part.slice(1, -1)}</code>
          );
        }
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} style={{ color: '#e2e8f0' }}>{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
      {streaming && (
        <span style={{ color: '#22d3ee', animation: 'chatPulse .7s infinite' }}>▌</span>
      )}
    </>
  );
}

// ── Single message bubble ─────────────────────────────────────────────────────
function MessageBubble({ msg, entity }) {
  const isMe = msg.from === 'me';

  // Typing indicator (three dots)
  if (msg.typing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
        <div style={{
          background: '#0a1120', border: '1px solid #1e293b',
          borderRadius: '3px 12px 12px 12px', padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 5, height: 5, borderRadius: '50%',
              background: entity?.color || '#22d3ee',
              animation: `chatTyping .9s ${i * 0.15}s infinite ease-in-out`,
            }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: isMe ? 'flex-end' : 'flex-start',
      marginBottom: 6,
    }}>
      <div style={{ maxWidth: '84%' }}>
        {!isMe && (
          <div style={{
            fontSize: 8, color: '#475569', marginBottom: 3,
            letterSpacing: '.08em', fontFamily: MONO,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {entity?.name}
            {msg.streaming && (
              <span style={{ color: entity?.color || '#22d3ee', fontSize: 7 }}>● streaming</span>
            )}
          </div>
        )}

        <div style={{
          padding: '9px 12px',
          borderRadius: isMe ? '12px 3px 12px 12px' : '3px 12px 12px 12px',
          background: isMe ? '#22d3ee14' : '#0a1120',
          border: `1px solid ${isMe ? '#22d3ee2a' : msg.streaming ? (entity?.color || '#22d3ee') + '30' : '#1e293b'}`,
          fontSize: 11, color: '#e2e8f0', lineHeight: 1.7,
          wordBreak: 'break-word',
          transition: 'border-color .2s',
        }}>
          {isMe
            ? msg.text
            : <MdText text={msg.text} streaming={msg.streaming} />
          }
        </div>

        <div style={{
          fontSize: 8, color: '#334155', marginTop: 3,
          textAlign: isMe ? 'right' : 'left', fontFamily: MONO,
          display: 'flex', gap: 6, justifyContent: isMe ? 'flex-end' : 'flex-start',
          alignItems: 'center',
        }}>
          {formatTime(msg.ts)}
          {isMe && (
            <span style={{ color: msg.status === 'read' ? '#22d3ee88' : '#334155' }}>
              {msg.status === 'read' ? '✓✓' : '✓'}
            </span>
          )}
          {msg.isOffline && (
            <span style={{ color: '#f59e0b', fontSize: 7 }}>OFFLINE MODE</span>
          )}
          {msg.isNudge && (
            <span style={{ color: '#a78bfa', fontSize: 7 }}>NEEDS CONFIG</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Platform context bar ──────────────────────────────────────────────────────
function ContextBar({ entity }) {
  const rt = window.__dd_ctx;
  if (!rt || entity.comingSoon) return null;

  return (
    <div style={{
      padding: '5px 12px',
      background: `${entity.color}06`,
      borderBottom: `1px solid ${entity.color}18`,
      fontSize: 8.5, color: '#475569',
      fontFamily: MONO,
      display: 'flex', alignItems: 'center', gap: 6,
      flexShrink: 0,
    }}>
      <span style={{ color: entity.color }}>◈</span>
      {rt.datasetName
        ? `Seeing: ${rt.datasetName} · ${rt.rowCount}r × ${rt.colCount}c · Q${rt.qualityScore}`
        : rt.module
          ? `On: ${rt.module}`
          : 'Platform context active'}
    </div>
  );
}

// ── Configure nudge bar ───────────────────────────────────────────────────────
function ConfigureBar({ entity, onNavigate }) {
  if (!entity.comingSoon) return null;
  return (
    <div style={{
      padding: '8px 12px',
      background: '#f59e0b08',
      borderBottom: '1px solid #f59e0b22',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 9, color: '#94a3b8', lineHeight: 1.5 }}>
        Configure a brain to activate this agent
      </span>
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('dd:navigate', { detail: 'local_agents' }))}
        style={{
          padding: '4px 9px', background: '#f59e0b18',
          border: '1px solid #f59e0b44', borderRadius: 2,
          color: '#f59e0b', fontSize: 8, cursor: 'pointer',
          fontFamily: MONO, fontWeight: 700, letterSpacing: '.08em',
          flexShrink: 0, whiteSpace: 'nowrap',
        }}
      >
        ⚙ SET UP →
      </button>
    </div>
  );
}

// ── Main ChatWindow ───────────────────────────────────────────────────────────
export default function ChatWindow({ entityId, minimized, zIndex, onFocus }) {
  const {
    convos, sendMessage, closeChat,
    toggleMinimize, abortStream, isStreaming,
    ollamaOk,
  } = useChat();

  // Always read fresh entity — picks up Config Studio changes instantly
  const entity   = getEntity(entityId);
  const messages = convos[entityId] || [];

  const [input,    setInput]    = useState('');
  const [expanded, setExpanded] = useState(false);

  const endRef   = useRef();
  const inputRef = useRef();

  const streaming = isStreaming(entityId);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!minimized) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, minimized]);

  const send = useCallback(() => {
    if (!input.trim() || streaming) return;
    sendMessage(entityId, input.trim());
    setInput('');
    inputRef.current?.focus();
  }, [entityId, input, sendMessage, streaming]);

  const handleStop = useCallback(() => {
    abortStream(entityId);
  }, [entityId, abortStream]);

  if (!entity) return null;

  const typePill     = TYPE_PILL[entity.type] || TYPE_PILL[ENTITY_TYPES.HUMAN];
  const isAI         = entity.type !== ENTITY_TYPES.HUMAN;
  const isLive       = !entity.comingSoon && !!entity.brainId;
  const quickPrompts = entity.quickPrompts || [];

  const winW = expanded ? 560 : 400;
  const winH = expanded ? 660 : 540;

  return (
    <div
      onClick={onFocus}
      style={{
        width: winW, flexShrink: 0,
        background: '#060b14',
        border: `1px solid ${minimized ? '#1e293b' : isLive ? entity.color + '30' : '#334155'}`,
        borderTop: isLive && !minimized ? `2px solid ${entity.color}` : undefined,
        borderRadius: minimized ? '4px 4px 0 0' : 4,
        boxShadow: minimized ? 'none' : isLive
          ? `0 -4px 40px rgba(0,0,0,.6), 0 0 0 1px ${entity.color}10`
          : '0 -4px 30px rgba(0,0,0,.5)',
        transition: 'width .2s, box-shadow .15s',
        zIndex,
        display: 'flex', flexDirection: 'column',
        height: minimized ? 'auto' : winH,
        overflow: 'hidden',
        animation: 'chatSlideUp .2s cubic-bezier(.16,1,.3,1)',
      }}
    >
      {/* ── HEADER ── */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 12px',
          background: '#020508',
          borderBottom: minimized ? 'none' : '1px solid #1e293b',
          cursor: 'pointer', flexShrink: 0, userSelect: 'none',
        }}
        onClick={() => toggleMinimize(entityId)}
      >
        {/* Avatar */}
        <div style={{
          width: 28, height: 28,
          borderRadius: isAI ? 3 : '50%',
          background: `${entity.color}22`,
          border: `1px solid ${isLive ? entity.color + '88' : entity.color + '44'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: isAI ? 13 : 11, color: entity.color,
          fontWeight: 800, flexShrink: 0, position: 'relative',
          boxShadow: isLive ? `0 0 8px ${entity.color}22` : 'none',
        }}>
          {entity.avatar[0]}
          {/* Online/live indicator */}
          <div style={{
            position: 'absolute', bottom: -1, right: -1,
            width: 7, height: 7, borderRadius: '50%',
            background: isLive ? entity.color : '#4ade80',
            border: '1.5px solid #020508',
            boxShadow: isLive ? `0 0 4px ${entity.color}` : 'none',
          }} />
        </div>

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#e2e8f0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {entity.name}
            </span>
            {/* <span style={{
              fontSize: 7, padding: '1px 5px', borderRadius: 2,
              background: typePill.bg, border: `1px solid ${typePill.border}`,
              color: typePill.color, letterSpacing: '.08em', flexShrink: 0,
            }}>
              {typePill.label}
            </span> */}
            {isLive ? (
              <span style={{
                fontSize: 7, padding: '1px 5px', borderRadius: 2,
                background: `${entity.color}18`, border: `1px solid ${entity.color}44`,
                color: entity.color, letterSpacing: '.08em', flexShrink: 0,
              }}>
                ● LIVE
              </span>
            ) : (
              <span style={{
                fontSize: 7, padding: '1px 5px', borderRadius: 2,
                background: '#f59e0b18', border: '1px solid #f59e0b33',
                color: '#f59e0b', letterSpacing: '.08em', flexShrink: 0,
              }}>
                SETUP
              </span>
            )}
          </div>
          {!minimized && (
            <div style={{
              fontSize: 8, color: '#475569',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: MONO,
            }}>
              {isLive
                ? `${entity.brainName || entity.brainId} · ${entity.role.split('·')[0].trim()}`
                : entity.role.split('·')[0].trim()
              }
            </div>
          )}
        </div>

        {/* Controls */}
        <div
          style={{ display: 'flex', gap: 3, flexShrink: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {streaming && (
            <button
              onClick={e => { e.stopPropagation(); handleStop(); }}
              title="Stop generating"
              style={{ ...ctrlBtn, color: '#f87171', fontSize: 9, fontFamily: MONO }}
            >■</button>
          )}
          <button
            onClick={e => {
              e.stopPropagation();
              setExpanded(!expanded);
              if (minimized) toggleMinimize(entityId);
            }}
            title={expanded ? 'Shrink' : 'Expand'}
            style={ctrlBtn}
          >{expanded ? '⊡' : '⊟'}</button>
          <button
            onClick={e => { e.stopPropagation(); closeChat(entityId); }}
            title="Close"
            style={{ ...ctrlBtn, color: '#f87171' }}
          >✕</button>
        </div>
      </div>

      {/* ── BODY ── */}
      {!minimized && (
        <>
          {/* Configure bar — shown when agent has no brain */}
          <ConfigureBar entity={entity} />

          {/* Platform context bar — shown when user has data loaded */}
          <ContextBar entity={entity} />

          {/* Expanded info panel */}
          {expanded && (
            <div style={{
              padding: '10px 14px',
              borderBottom: '1px solid #1e293b',
              background: '#0a112044', flexShrink: 0,
            }}>
              <p style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.6, marginBottom: 6 }}>
                {entity.description}
              </p>
              {isLive && (
                <div style={{
                  fontSize: 9, color: entity.color, marginBottom: 8,
                  fontFamily: MONO,
                }}>
                  Brain: {entity.brainName || entity.brainId}
                  {entity.temperature !== undefined && ` · T=${entity.temperature}`}
                  {entity.chainOfThought && ' · CoT'}
                  {entity.ragEnabled && ' · RAG'}
                </div>
              )}
              {entity.capabilities && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {entity.capabilities.map(c => (
                    <span key={c} style={{
                      fontSize: 8, padding: '2px 7px',
                      background: '#1e293b', borderRadius: 2, color: '#475569',
                    }}>{c}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Ollama offline banner */}
          {ollamaOk === false && isLive && (
            <div style={{
              padding: '6px 12px',
              background: '#f59e0b08', borderBottom: '1px solid #f59e0b22',
              fontSize: 8.5, color: '#f59e0b', fontFamily: MONO, lineHeight: 1.6,
              flexShrink: 0,
            }}>
              ⚠ Ollama offline — run: <code style={{ color: '#22d3ee' }}>ollama serve</code>
            </div>
          )}

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '10px 12px',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {messages.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '20px 0',
                fontSize: 10, color: '#334155', lineHeight: 1.8,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: isAI ? 6 : '50%',
                  background: `${entity.color}18`,
                  border: `1px solid ${entity.color}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, margin: '0 auto 12px',
                }}>
                  {entity.avatar[0]}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                  {isLive
                    ? `${entity.name} is ready`
                    : `Configure ${entity.name} to get started`}
                </div>
                {isAI && isLive && quickPrompts.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {quickPrompts.slice(0, 3).map(p => (
                      <button key={p} onClick={() => setInput(p)} style={{
                        padding: '6px 10px', background: '#0a1120',
                        border: '1px solid #1e293b', color: '#475569',
                        fontSize: 9, cursor: 'pointer', borderRadius: 2,
                        fontFamily: MONO, transition: 'all .15s', textAlign: 'left',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.color = entity.color; e.currentTarget.style.borderColor = entity.color + '44'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#1e293b'; }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              messages.map((msg, i) => (
                <MessageBubble key={msg.id || i} msg={msg} entity={entity} />
              ))
            )}
            <div ref={endRef} />
          </div>

          {/* Quick prompts strip (after conversation starts) */}
          {isAI && isLive && messages.length > 0 && !streaming && (
            <div style={{
              padding: '4px 10px',
              display: 'flex', gap: 4, overflowX: 'auto',
              flexShrink: 0,
            }}>
              {quickPrompts.slice(0, 3).map(p => (
                <button key={p} onClick={() => setInput(p)} style={{
                  padding: '3px 9px', background: 'transparent',
                  border: '1px solid #1e293b', color: '#475569',
                  fontSize: 8, cursor: 'pointer', borderRadius: 2,
                  fontFamily: MONO, whiteSpace: 'nowrap',
                  flexShrink: 0, transition: 'all .15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = entity.color; e.currentTarget.style.borderColor = entity.color + '44'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#1e293b'; }}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <div style={{
            padding: '8px 10px',
            borderTop: '1px solid #1e293b',
            background: '#020508', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  streaming
                    ? 'Generating…'
                    : isLive
                      ? `Ask ${entity.name}… (Enter to send)`
                      : `Configure ${entity.name} to chat…`
                }
                disabled={streaming}
                rows={expanded ? 2 : 1}
                style={{
                  flex: 1, padding: '7px 10px',
                  background: '#0a1120',
                  border: `1px solid ${streaming ? entity.color + '44' : '#334155'}`,
                  color: '#e2e8f0', fontSize: 11,
                  borderRadius: 3, outline: 'none',
                  fontFamily: 'system-ui,sans-serif',
                  resize: 'none', lineHeight: 1.5,
                  transition: 'border-color .15s',
                  opacity: streaming ? 0.6 : 1,
                }}
                onFocus={e => e.target.style.borderColor = entity.color + '66'}
                onBlur={e => e.target.style.borderColor = streaming ? entity.color + '44' : '#334155'}
              />
              {streaming ? (
                <button
                  onClick={handleStop}
                  style={{
                    width: 32, height: 32, flexShrink: 0,
                    background: '#f8717118', border: '1px solid #f8717144',
                    borderRadius: 3, cursor: 'pointer', color: '#f87171',
                    fontSize: 11, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontFamily: MONO,
                  }}
                >■</button>
              ) : (
                <button
                  onClick={send}
                  disabled={!input.trim() || !isLive}
                  style={{
                    width: 32, height: 32, flexShrink: 0,
                    background: input.trim() && isLive ? entity.color : '#0a1120',
                    border: 'none', borderRadius: 3,
                    cursor: input.trim() && isLive ? 'pointer' : 'not-allowed',
                    color: input.trim() && isLive ? '#020508' : '#334155',
                    fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s',
                  }}
                >↑</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const ctrlBtn = {
  width: 18, height: 18, background: 'transparent', border: 'none',
  color: '#475569', cursor: 'pointer', fontSize: 11, borderRadius: 2,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'color .15s', lineHeight: 1,
};
